import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import type { ReactNode } from 'react';

import type { PipelineCardDetail as PipelineCardDetailDto } from '@/pages/pipelines/common/types';
import { getPipelineCardPresentation } from '@/pages/pipelines/common/utils/card-presentation';

import {
  getPipelineDetailActions,
  getPipelineDetailHeaderAction,
  getPipelineDetailInlineMetaAction,
  getPipelineDetailPrimaryAction,
} from './actions';
import { PipelineDetailHeader } from './header';
import {
  getPipelineDetailBadges,
  getPipelineDetailTimeline,
  getPipelineIndicatorRows,
} from './presentation';
import { PipelineDetailSections } from './sections';
import {
  DetailBody,
  DetailContent,
  DetailDivider,
  DetailFooter,
  DetailFooterButton,
  DetailRow,
  DetailRowLabel,
  DetailRowValue,
  DetailSection,
} from './styled';
import { PipelineDetailTimeline } from './timeline';

type Props = {
  detail: PipelineCardDetailDto;
  onNavigate: (_href: string | null | undefined) => void;
  extraContent?: ReactNode;
};

export const PipelineDetailContent = ({ detail, onNavigate, extraContent }: Props) => {
  const detailActions = getPipelineDetailActions(detail);
  const headerAction = getPipelineDetailHeaderAction(detailActions);
  const inlineMetaAction = getPipelineDetailInlineMetaAction(detailActions);
  const primaryAction = getPipelineDetailPrimaryAction(detailActions);
  const presentation = getPipelineCardPresentation(detail);
  const detailBadges = getPipelineDetailBadges(detail, presentation.badges);
  const detailIndicatorRows = getPipelineIndicatorRows(detail);
  const detailTimeline = getPipelineDetailTimeline(detail);

  return (
    <>
      <DetailContent>
        <PipelineDetailHeader
          detail={detail}
          title={presentation.title}
          badges={detailBadges}
          headerAction={headerAction}
          inlineMetaAction={inlineMetaAction}
          onNavigate={onNavigate}
        />

        <DetailDivider />

        <DetailBody>
          <PipelineDetailSections detail={detail} />

          {extraContent}

          {detailIndicatorRows.length > 0 && (
            <DetailSection>
              {detailIndicatorRows.map((row) => (
                <DetailRow key={row.key}>
                  <DetailRowLabel>{row.label}</DetailRowLabel>
                  <DetailRowValue data-tone={row.tone}>{row.value}</DetailRowValue>
                </DetailRow>
              ))}
            </DetailSection>
          )}

          {detailTimeline.length > 0 && <PipelineDetailTimeline items={detailTimeline} />}
        </DetailBody>
      </DetailContent>

      <DetailFooter>
        <BaseDialog.Close
          render={
            <DetailFooterButton color="tertiary" variant="outline">
              Close
            </DetailFooterButton>
          }
        />
        <DetailFooterButton
          color="primary"
          variant="solid"
          disabled={!primaryAction?.href}
          onClick={() => primaryAction?.href && onNavigate(primaryAction.href)}
        >
          {primaryAction?.label ?? 'Open'}
        </DetailFooterButton>
      </DetailFooter>
    </>
  );
};
